import { useState, useRef } from 'react';
import { Stack, Group, Button, Image, Text, SimpleGrid, ActionIcon, Paper, Badge } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { extractPhotoMetadata, type PhotoMetadata } from '../lib/exif';

interface Props {
  photos?: File[];
  onChange: (photos: File[]) => void;
  onGpsExtracted?: (gps: { lat: number; lng: number }) => void;
  onExifMetadata?: (metadata: PhotoMetadata) => void;
  maxPhotos?: number;
}

export default function PhotoCapture({ photos = [], onChange, onGpsExtracted, onExifMetadata, maxPhotos = 10 }: Props) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [gpsFound, setGpsFound] = useState(false);

  const handleFiles = async (files: File[]) => {
    const newPhotos = [...photos];
    const newPreviews = [...previews];

    for (const file of files) {
      if (newPhotos.length >= maxPhotos) break;
      newPhotos.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    setPreviews(newPreviews);
    onChange(newPhotos);

    for (const file of files) {
      try {
        const metadata = await extractPhotoMetadata(file);
        if (metadata?.gps) {
          setGpsFound(true);
          if (onGpsExtracted) onGpsExtracted(metadata.gps);
          if (onExifMetadata) onExifMetadata(metadata);
          break;
        }
      } catch {
        // EXIF extraction failed
      }
    }
  };

  const handleCamera = () => {
    cameraRef.current?.click();
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) handleFiles(files);
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    URL.revokeObjectURL(previews[index]);
    setPreviews(newPreviews);
    onChange(newPhotos);
  };

  return (
    <Stack gap="sm">
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraCapture}
      />

      <Group gap="sm">
        <Button
          leftSection={<i className="fa-solid fa-camera" />}
          onClick={handleCamera}
          variant="filled"
          size="md"
        >
          Take Photo
        </Button>
        <Text size="xs" c="dimmed">{photos.length}/{maxPhotos} photos</Text>
        {gpsFound && (
          <Badge size="xs" variant="light" color="green" leftSection={<span style={{ fontSize: 10 }}>📍</span>}>
            Location extracted from photo
          </Badge>
        )}
      </Group>

      <Dropzone
        onDrop={handleFiles}
        accept={IMAGE_MIME_TYPE}
        maxSize={15 * 1024 * 1024}
        disabled={photos.length >= maxPhotos}
        styles={{ root: { minHeight: 80, borderStyle: 'dashed' } }}
      >
        <Group justify="center" gap="sm" py="sm">
          <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 20, opacity: 0.5 }} />
          <Text size="sm" c="dimmed">Drop photos here or click to browse</Text>
        </Group>
      </Dropzone>

      {previews.length > 0 && (
        <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="xs">
          {previews.map((src, i) => (
            <Paper key={i} pos="relative" withBorder radius="sm" style={{ overflow: 'hidden' }}>
              <Image src={src} h={80} fit="cover" />
              <ActionIcon
                size="xs"
                color="red"
                variant="filled"
                pos="absolute"
                top={4}
                right={4}
                onClick={() => removePhoto(i)}
              >
                <i className="fa-solid fa-xmark" style={{ fontSize: 10 }} />
              </ActionIcon>
            </Paper>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
